import React, { useState, useEffect } from 'react';
import { Table, Card, Button, message, Tag, Modal, Form, Input, Select, Space, InputNumber, Row, Col } from 'antd';
import { PlusOutlined, EditOutlined, StopOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { clinicService } from '../../services';
import CurrencySelector from '../../components/CurrencySelector/CurrencySelector';
import { useApp } from '../../context/AppContext';

const { Option } = Select;

const ClinicUsers = () => {
    const { t } = useTranslation(['common', 'dentist']);
    const { user: currentUser } = useApp();
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(false);
    const [modalVisible, setModalVisible] = useState(false);
    const [selectedRole, setSelectedRole] = useState('receptionist');
    const [form] = Form.useForm();
    const [allowedRoles, setAllowedRoles] = useState(['dentist', 'receptionist', 'pharmacy', 'lab_technician', 'radiologist']);

    useEffect(() => {
        fetchUsers();
        initializeAllowedRoles();
    }, []);

    const initializeAllowedRoles = () => {
        try {
            if (currentUser && currentUser.allowedRoles && currentUser.allowedRoles.length > 0) {
                setAllowedRoles(currentUser.allowedRoles);
                // Set first allowed role as default if current default is not allowed
                if (!currentUser.allowedRoles.includes(selectedRole)) {
                    setSelectedRole(currentUser.allowedRoles[0]);
                }
            }
        } catch (error) {
            // If error, show all roles (backward compatibility)
            console.log('Could not fetch current user permissions');
        }
    };

    const fetchUsers = async () => {
        setLoading(true);
        try {
            const response = await clinicService.getUsers();
            if (response.success) {
                setUsers(response.data);
            }
        } catch (error) {
            message.error('Failed to fetch users');
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = async (values) => {
        try {
            // Add createUserAccount flag for dentists to also create Dentist record
            if (values.role === 'dentist') {
                values.createUserAccount = true;
            }
            await clinicService.createUser(values);
            message.success('User created successfully');
            setModalVisible(false);
            form.resetFields();
            setSelectedRole('receptionist');
            fetchUsers();
        } catch (error) {
            message.error(error.response?.data?.message || 'Failed to create user');
        }
    };

    const handleRoleChange = (value) => {
        setSelectedRole(value);
    };

    const columns = [
        {
            title: 'Name',
            dataIndex: 'name',
            key: 'name'
        },
        {
            title: 'Email',
            dataIndex: 'email',
            key: 'email'
        },
        {
            title: 'Role',
            dataIndex: 'role',
            key: 'role',
            render: (role) => {
                const colors = {
                    admin: 'red',
                    dentist: 'blue',
                    receptionist: 'green',
                    pharmacy: 'purple',
                    lab_technician: 'orange',
                    radiologist: 'cyan'
                };
                return <Tag color={colors[role] || 'default'}>{role.toUpperCase().replace('_', ' ')}</Tag>;
            }
        },
        {
            title: 'Phone',
            dataIndex: 'phone',
            key: 'phone'
        },
        {
            title: 'Status',
            dataIndex: 'isActive',
            key: 'isActive',
            render: (isActive) => (
                <Tag color={isActive ? 'success' : 'error'}>
                    {isActive ? 'Active' : 'Inactive'}
                </Tag>
            )
        },
        {
            title: 'Actions',
            key: 'actions',
            render: () => (
                <Space>
                    <Button type="link">Edit</Button>
                    <Button type="link" danger>Deactivate</Button>
                </Space>
            )
        }
    ];

    return (
        <div>
            <Card>
                <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between' }}>
                    <h1>User Management</h1>
                    <Button type="primary" icon={<PlusOutlined />} onClick={() => setModalVisible(true)}>
                        Add User
                    </Button>
                </div>
                <Table
                    columns={columns}
                    dataSource={users}
                    rowKey="_id"
                    loading={loading}
                    scroll={{ x: 'max-content' }}
                />
            </Card>

            <Modal
                title="Add New User"
                open={modalVisible}
                onCancel={() => {
                    setModalVisible(false);
                    form.resetFields();
                    setSelectedRole('receptionist');
                }}
                footer={null}
                width={700}
            >
                <Form form={form} layout="vertical" onFinish={handleSubmit}>
                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item name="name" label="Name" rules={[{ required: true }]}>
                                <Input />
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item name="email" label="Email" rules={[{ required: true, type: 'email' }]}>
                                <Input />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item name="password" label="Password" rules={[{ required: true, min: 6 }]}>
                                <Input.Password />
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item name="role" label="Role" rules={[{ required: true }]} initialValue={allowedRoles[0] || 'receptionist'}>
                                <Select onChange={handleRoleChange}>
                                    {allowedRoles.includes('dentist') && <Option value="dentist">Dentist</Option>}
                                    {allowedRoles.includes('receptionist') && <Option value="receptionist">Secretary/Receptionist</Option>}
                                    {allowedRoles.includes('pharmacy') && <Option value="pharmacy">Pharmacy Staff</Option>}
                                    {allowedRoles.includes('lab_technician') && <Option value="lab_technician">Lab Technician</Option>}
                                    {allowedRoles.includes('radiologist') && <Option value="radiologist">Radiologist</Option>}
                                </Select>
                            </Form.Item>
                        </Col>
                    </Row>

                    <Form.Item name="phone" label="Phone">
                        <Input />
                    </Form.Item>

                    {/* Dentist-specific fields */}
                    {selectedRole === 'dentist' && (
                        <>
                            <Row gutter={16}>
                                <Col span={12}>
                                    <Form.Item
                                        name="specialization"
                                        label={t('dentist:specialization')}
                                        rules={[{ required: true, message: 'Please select specialization' }]}
                                    >
                                        <Select>
                                            <Option value="General Dentistry">General Dentistry</Option>
                                            <Option value="Orthodontics">Orthodontics</Option>
                                            <Option value="Periodontics">Periodontics</Option>
                                            <Option value="Endodontics">Endodontics</Option>
                                            <Option value="Oral Surgery">Oral Surgery</Option>
                                            <Option value="Pediatric Dentistry">Pediatric Dentistry</Option>
                                            <Option value="Prosthodontics">Prosthodontics</Option>
                                        </Select>
                                    </Form.Item>
                                </Col>
                                <Col span={12}>
                                    <Form.Item
                                        name="qualification"
                                        label={t('dentist:qualification')}
                                        rules={[{ required: true, message: 'Please enter qualification' }]}
                                    >
                                        <Input placeholder="e.g., DDS, BDS, DMD" />
                                    </Form.Item>
                                </Col>
                            </Row>

                            <Row gutter={16}>
                                <Col span={12}>
                                    <Form.Item
                                        name="consultationFee"
                                        label={t('dentist:consultationFee')}
                                        rules={[{ required: true, message: 'Please enter consultation fee' }]}
                                    >
                                        <InputNumber min={0} style={{ width: '100%' }} />
                                    </Form.Item>
                                </Col>
                                <Col span={12}>
                                    <Form.Item
                                        name="currency"
                                        label={t('common:currency.selectCurrency')}
                                        initialValue="USD"
                                    >
                                        <CurrencySelector />
                                    </Form.Item>
                                </Col>
                            </Row>

                            <Form.Item name="licenseNumber" label="License Number">
                                <Input />
                            </Form.Item>
                        </>
                    )}

                    <Form.Item>
                        <Button type="primary" htmlType="submit" block size="large">
                            Create {selectedRole === 'dentist' ? 'Dentist' : 'Secretary'} Account
                        </Button>
                    </Form.Item>
                </Form>
            </Modal>
        </div>
    );
};

export default ClinicUsers;
